import { Delete, Group, GroupAdd, Lock, SubdirectoryArrowRight, SupervisedUserCircle } from '@mui/icons-material';
import { Typography, Button, List, ListItem, ListItemIcon, IconButton, Box, SvgIconProps } from '@mui/material';
import { useState, useCallback, useEffect } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useTranslation } from 'react-i18next';
import { SelectionModal } from 'src/modules/groupMessageEmail/components/selectionModal';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { Colors } from 'src/shared/components/styles/colors';
import { SelectionTypeEnum } from '../models/selectionType.enum';
import { FixedSelection } from '../models/fixedSelection';

type SelectionFormProps = {
  smsMode?: boolean;
  disabled?: boolean;
  selectionFromServer: Selection | null;
  fixedSelectionFromServer: FixedSelection | null;
  onSelection: (selection: Selection | null) => void;
  onFixedSelection: (selection: FixedSelection | null) => void;
};

export const SelectionForm = (props: SelectionFormProps) => {
  const { smsMode = false, disabled = false, selectionFromServer, fixedSelectionFromServer, onSelection, onFixedSelection } = props;
  const { t } = useTranslation(['groupMessage', 'common']);
  const formProp = 'selection_id';
  const { setValue, trigger } = useFormContext();

  const { errors } = useFormState();

  const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const showSeletionModal = () => {
    setModalOpen(true);
  };

  const closeDialog = useCallback(() => {
    setModalOpen(false);
  }, []);

  const resetGroupSelection = () => {
    setSelection(null);
    onSelection(null);
    setFixedSelection(null);
    onFixedSelection(null);
    setSelection(null);
    setValue('excludedMemberFromSelection', undefined);
    setSelectedRows(new Set());
  };

  const onSaveSelection = useCallback(
    (newSelection: Selection | null, newFixedSelection: FixedSelection | null) => {
      if (newSelection) {
        setSelection(newSelection);
        setFixedSelection(null);
      } else {
        setFixedSelection(newFixedSelection);
        setSelection(null);
      }
      setTimeout(() => {
        setModalOpen(false);
        trigger();
      }, 100);
    },
    [trigger],
  );

  useEffect(() => {
    setSelection(selectionFromServer);
  }, [selectionFromServer]);

  useEffect(() => {
    setFixedSelection(fixedSelectionFromServer);
  }, [fixedSelectionFromServer]);

  useEffect(() => {
    setValue('selection_id', selection ? selection.selection_id : 0);
    if (selection) {
      onSelection(selection);
      onFixedSelection(null);
      setValue('speedFormValues', '');
      setValue('speedFormText', '');
      setValue('selectionType', SelectionTypeEnum.Selection);
    }
  }, [onFixedSelection, onSelection, selection, setValue]);

  useEffect(() => {
    setValue('speedFormValues', fixedSelection ? fixedSelection.value : '');
    setValue('speedFormText', fixedSelection ? fixedSelection.name : '');
    if (fixedSelection) {
      onSelection(null);
      onFixedSelection(fixedSelection);
      setValue('selection_id', 0);
      setValue('selectionType', SelectionTypeEnum.FixedSelection);
    }
  }, [fixedSelection, onFixedSelection, onSelection, setValue]);

  let errorMessage = '';
  if (errors && Object.prototype.hasOwnProperty.call(errors, formProp)) {
    errorMessage = errors.selection_id?.message?.toString() || '';
  }

  const DisplaySelection = ({ icon, text }: { icon: React.ReactElement<SvgIconProps>; text: string }) => {
    return (
      <>
        <ListItem
          secondaryAction={
            <IconButton size='small' onClick={resetGroupSelection} aria-label={t('groupMessage:selection.removeSelection')} disabled={disabled}>
              {disabled ? <Lock sx={{ color: 'text.disabled' }} /> : <Delete />}
            </IconButton>
          }
          sx={{
            px: 0,
            pr: '0 !important',
            p: 1,
            borderRadius: 1,
            border: `solid 1px ${Colors.Form.OutlinedInput}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'start',
            backgroundColor: disabled ? Colors.Form.DisabledInput : null,
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <ListItemIcon sx={{ minWidth: '2rem !important', justifyContent: 'center', pr: 1 }}>{icon}</ListItemIcon>
            <Typography component='span' variant='body1' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {text}
            </Typography>
          </Box>
          {selection && (
            <Box sx={{ display: 'flex' }}>
              <ListItemIcon sx={{ minWidth: '2rem !important', justifyContent: 'center', pr: 1 }}>
                <SubdirectoryArrowRight sx={{ fontSize: '16px' }} />
              </ListItemIcon>
              {smsMode ? (
                <Typography component='span' variant='body2' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t('groupMessage:email.modifiedSelection', {
                    count: selection?.memberCountWithMobilePhone && selection?.memberCountWithMobilePhone,
                  })}
                </Typography>
              ) : (
                <>
                  {disabled ? (
                    // Lägger in detta då så länge antalet visas fel - KH & SN 2024-11-07
                    <Typography component='span' variant='body2' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t('groupMessage:email.modifiedSelectionWithoutCount')}
                    </Typography>
                  ) : (
                    <Typography component='span' variant='body2' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t('groupMessage:email.modifiedSelection', {
                        count: selection?.memberCountWithEmail ? selection?.memberCountWithEmail - selectedRows.size : 0,
                      })}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </ListItem>
      </>
    );
  };

  return (
    <>
      <ControlledTextField name='speedFormValues' type='hidden' />
      <ControlledTextField name='speedFormText' type='hidden' />
      <ControlledTextField name='selection_id' type='hidden' />
      <SelectionModal
        activeSelection={selectionFromServer}
        activeFixedSelection={fixedSelectionFromServer}
        modalOpen={modalOpen}
        onSave={onSaveSelection}
        onClose={closeDialog}
        smsMode={smsMode !== undefined ? smsMode : false}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        selection={selection}
        setSelection={setSelection}
        // setModifiedSelectionCount={setModifiedSelectionCount}
      />
      <ContentSection header={t('groupMessage:selection.recipients')} hasError={!!errorMessage}>
        <List
          dense
          sx={{
            '& .MuiListItemSecondaryAction-root': {
              right: (thm) => thm.spacing(1),
            },
          }}
        >
          {!selection && !fixedSelection && !disabled && (
            <ListItem
              key='no-selection'
              sx={{
                p: 1,
                background: '#f8f8f8',
                borderRadius: 1,
                border: (thm) => `solid 1px ${thm.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography component='em' variant='body1' sx={{ pl: 0.5, color: 'grey.400' }}>
                {t('groupMessage:selection.noSelectionMessage')} {errorMessage && <span> ({errorMessage})</span>}
              </Typography>

              <Button
                variant='outlined'
                sx={{ ml: 1, backgroundColor: 'white' }}
                size='small'
                startIcon={<GroupAdd />}
                onClick={showSeletionModal}
                disableElevation
              >
                {t('common:button.choose')} {t('groupMessage:selection.recipients')}
              </Button>
            </ListItem>
          )}
          {selection && (
            <DisplaySelection
              icon={<Group fontSize='small' />}
              text={disabled ? selection.selectionName : `${selection.selectionName} (${selection.memberCount})`} // Lägger in detta då så länge antalet visas fel - KH & SN 2024-11-07
            />
          )}
          {fixedSelection && <DisplaySelection icon={<SupervisedUserCircle fontSize='small' />} text={`${fixedSelection.name}`} />}
        </List>
      </ContentSection>
    </>
  );
};
